// Link giới thiệu khi thêm đơn vị (docs/KE_HOACH_BANG_GIA_KHI_TAO_DON_VI.md §4.2): chọn loại, bảng giá (mặc định Gán sau),
// bảng chiết khấu (CTV), giao diện (Đại lý / CTV) → một link + mã QR cho đúng bộ lựa chọn đó. Link đã gửi giữ nguyên
// lựa chọn lúc gửi; chọn lại đúng bộ cũ thì hiện lại link cũ. "Link đang mở" liệt kê link kèm số đã đăng ký, thu hồi được.
// Dùng chung: Đại lý / CTV (`/network/invites`, API web) và khu NPP (`/shops/invites`, admin-api) — người gọi truyền API.

import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Check, Copy, Download, Link2, Share2 } from 'lucide-react'
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react'
import { toast } from 'sonner'
import { WORK_MODE_LABEL, type WorkMode } from '@shoptrack/common/unit-permissions'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { IndexBadge } from '@/components/ui/data-table'
import { Segmented } from '@/components/ui/segmented'
import { confirm } from '@/components/ui/confirm'
import { ALL_WORK_MODES, WorkModePicker } from './WorkModePicker'
import { priceLaterWarning, schemeLaterWarning } from './SetupPicker'

type InviteType = 'agency' | 'ctv' | 'shop'

const TYPE_LABEL: Record<InviteType, string> = { agency: 'Đại lý', ctv: 'CTV', shop: 'Shop' }

export interface InviteDto {
  id: string
  code: string
  unitType: InviteType
  priceList: { id: string; code: string; name: string } | null
  scheme: { id: string; code: string; name: string } | null
  workModes: WorkMode[]
  signups: number
  createdAt: string
}

export interface InviteApi {
  get: <T>(path: string) => Promise<T>
  post: <T>(path: string, body?: unknown) => Promise<T>
}

interface Choice {
  unitType: InviteType
  /** '' = gán sau */
  priceListId: string
  schemeId: string
  workModes: WorkMode[]
}

const hasModes = (t: InviteType) => t !== 'shop'
const sameModes = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((m) => b.includes(m))

function matches(i: InviteDto, c: Choice): boolean {
  return (
    i.unitType === c.unitType &&
    (i.priceList?.id ?? '') === c.priceListId &&
    (c.unitType === 'ctv' ? (i.scheme?.id ?? '') === c.schemeId : true) &&
    (hasModes(c.unitType) ? sameModes(i.workModes, c.workModes) : true)
  )
}

export const inviteLink = (code: string) => `${window.location.origin}/signup?ref=${encodeURIComponent(code)}`

function modesText(m: readonly WorkMode[]): string | null {
  return m.length === 1 ? `Chỉ ${WORK_MODE_LABEL[m[0]!]}` : null
}

export function InviteLinkPanel({
  types,
  path,
  api,
  queryKey,
  priceLists,
  schemes,
  canChoosePrice,
}: {
  /** Loại mời được (đúng loại người mời được tạo) */
  types: InviteType[]
  /** '/network/invites' | '/shops/invites' */
  path: string
  api: InviteApi
  queryKey: readonly unknown[]
  /** Thư viện bảng giá của người mời (mọi loại — lọc theo loại đang chọn) */
  priceLists: Array<{ id: string; code: string; name: string; kind: string }>
  /** Bảng chiết khấu của người mời — null: không chọn được (CTV mời, hoặc không có quyền) */
  schemes: Array<{ id: string; code: string; name: string }> | null
  /** Có quyền chọn bảng cho link (không có: chỉ Gán sau) */
  canChoosePrice: boolean
}) {
  const qc = useQueryClient()
  const [choice, setChoice] = useState<Choice>(() => ({
    unitType: types.includes('shop') ? 'shop' : types[0]!,
    priceListId: '',
    schemeId: '',
    workModes: ALL_WORK_MODES,
  }))
  const listQ = useQuery({ queryKey, queryFn: () => api.get<{ invites: InviteDto[] }>(path) })
  const invites = listQ.data?.invites ?? []
  const current = invites.find((i) => matches(i, choice)) ?? null

  const create = useMutation({
    mutationFn: () =>
      api.post<{ invite: InviteDto; capWarning: string | null }>(path, {
        unitType: choice.unitType,
        priceListId: choice.priceListId || null,
        ...(choice.unitType === 'ctv' && schemes ? { schemeId: choice.schemeId || null } : {}),
        ...(hasModes(choice.unitType) ? { workModes: choice.workModes } : {}),
      }),
    onSuccess: (r) => {
      if (r.capWarning) toast.warning(r.capWarning)
      qc.setQueryData<{ invites: InviteDto[] }>(queryKey, (old) => ({ invites: [r.invite, ...(old?.invites ?? []).filter((i) => i.id !== r.invite.id)] }))
      qc.invalidateQueries({ queryKey })
    },
    onError: (e) => toast.error((e as Error).message),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api.post(`${path}/${id}/revoke`),
    onSuccess: () => {
      toast.success('Đã thu hồi link')
      qc.invalidateQueries({ queryKey })
    },
    onError: (e) => toast.error((e as Error).message),
  })

  async function askRevoke(i: InviteDto) {
    const ok = await confirm({ title: `Thu hồi link ${i.code}?`, description: 'Link này không dùng để đăng ký được nữa.', confirmLabel: 'Thu hồi', tone: 'danger' })
    if (ok) revoke.mutate(i.id)
  }

  const lists = priceLists.filter((l) => l.kind === choice.unitType)
  const set = (patch: Partial<Choice>) => setChoice((c) => ({ ...c, ...patch }))
  const select = 'h-11 w-full rounded-md border border-rule bg-paper px-2 text-sm desktop:h-9'

  return (
    <div className="space-y-4">
      {types.length > 1 && (
        <div className="space-y-1.5">
          <span className="block text-sm text-inkSoft">Loại</span>
          <Segmented
            value={choice.unitType}
            onChange={(t) => set({ unitType: t, priceListId: '', schemeId: '' })}
            options={types.map((t) => [t, TYPE_LABEL[t]] as const)}
            label="Loại đơn vị"
            className="text-sm"
          />
        </div>
      )}

      <label className="block space-y-1.5">
        <span className="text-sm text-inkSoft">Bảng giá</span>
        <select className={select} value={choice.priceListId} onChange={(e) => set({ priceListId: e.target.value })} disabled={!canChoosePrice}>
          <option value="">Gán sau</option>
          {lists.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name} ({l.code})
            </option>
          ))}
        </select>
      </label>

      {choice.unitType === 'ctv' && schemes && (
        <label className="block space-y-1.5">
          <span className="text-sm text-inkSoft">Bảng chiết khấu</span>
          <select className={select} value={choice.schemeId} onChange={(e) => set({ schemeId: e.target.value })} disabled={!canChoosePrice}>
            <option value="">Gán sau</option>
            {schemes.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name} ({x.code})
              </option>
            ))}
          </select>
        </label>
      )}

      {hasModes(choice.unitType) && <WorkModePicker value={choice.workModes} onChange={(workModes) => set({ workModes })} />}

      {(!choice.priceListId || (choice.unitType === 'ctv' && schemes && !choice.schemeId)) && (
        <div className="space-y-1.5" role="status">
          {!choice.priceListId && <Warn text={priceLaterWarning('invite')} />}
          {choice.unitType === 'ctv' && schemes && !choice.schemeId && <Warn text={schemeLaterWarning(true)} />}
        </div>
      )}

      {listQ.isLoading ? (
        <p className="text-sm text-muted">Đang tải…</p>
      ) : current ? (
        <LinkBlock invite={current} />
      ) : (
        <Button type="button" onClick={() => create.mutate()} disabled={create.isPending || listQ.isError} className="gap-1.5">
          <Link2 className="size-4" aria-hidden />
          {create.isPending ? 'Đang tạo…' : 'Tạo link'}
        </Button>
      )}

      {invites.length > 0 && (
        <section className="space-y-2 border-t border-rule pt-4">
          <h3 className="text-sm font-medium text-ink">Link đang mở</h3>
          <ul className="divide-y divide-rule rounded-lg border border-rule">
            {invites.map((i, idx) => {
              const on = current?.id === i.id
              const modes = hasModes(i.unitType) ? modesText(i.workModes) : null
              return (
                <li key={i.id} className={cn('flex items-center gap-2 px-3 py-2', on && 'bg-accent/5')}>
                  <IndexBadge n={idx + 1} />
                  <button
                    type="button"
                    className="min-w-0 flex-1 py-1 text-left"
                    aria-current={on || undefined}
                    onClick={() =>
                      setChoice({
                        unitType: i.unitType,
                        priceListId: i.priceList?.id ?? '',
                        schemeId: i.scheme?.id ?? '',
                        workModes: hasModes(i.unitType) && i.workModes.length ? i.workModes : ALL_WORK_MODES,
                      })
                    }
                  >
                    <span className="block truncate text-sm font-medium text-ink">
                      {TYPE_LABEL[i.unitType]}
                      {modes && <span className="font-normal text-inkSoft"> · {modes}</span>}
                      <span className="ml-1.5 font-mono text-xs font-normal text-muted">{i.code}</span>
                    </span>
                    <span className="block text-xs text-inkSoft">
                      Bảng giá: {i.priceList ? i.priceList.code : 'Gán sau'}
                      {i.unitType === 'ctv' && <> · Chiết khấu: {i.scheme ? i.scheme.code : 'Gán sau'}</>}
                      {' · '}
                      <span className="tabular-nums">{i.signups.toLocaleString('vi-VN')}</span> đã đăng ký
                    </span>
                  </button>
                  <Button type="button" size="sm" variant="ghost" className="shrink-0 text-danger hover:text-danger" disabled={revoke.isPending} onClick={() => askRevoke(i)}>
                    Thu hồi
                  </Button>
                </li>
              )
            })}
          </ul>
        </section>
      )}
    </div>
  )
}

function Warn({ text }: { text: string }) {
  return (
    <p className="flex items-start gap-2 rounded-lg bg-warn/10 px-3 py-2 text-sm text-ink">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" aria-hidden />
      <span>{text}</span>
    </p>
  )
}

/** QR + link + Sao chép / Chia sẻ / Tải QR (như ShopReferralCard). */
function LinkBlock({ invite }: { invite: InviteDto }) {
  const link = inviteLink(invite.code)
  const [copied, setCopied] = useState(false)
  const canvasWrap = useRef<HTMLDivElement>(null)
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  function copy() {
    navigator.clipboard.writeText(link).then(
      () => {
        setCopied(true)
        toast.success('Đã sao chép link giới thiệu')
        setTimeout(() => setCopied(false), 2000)
      },
      () => toast.error('Trình duyệt chặn sao chép'),
    )
  }
  function share() {
    navigator.share({ title: 'ShopTrack', url: link }).catch(() => {
      /* người dùng đóng hộp chia sẻ */
    })
  }
  function downloadQr() {
    const canvas = canvasWrap.current?.querySelector('canvas')
    if (!canvas) return
    const a = document.createElement('a')
    a.href = canvas.toDataURL('image/png')
    a.download = `shoptrack-gioi-thieu-${invite.code}.png`
    a.click()
  }

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start" data-invite-code={invite.code}>
      <div className="self-start shrink-0 rounded-lg border border-rule bg-white p-2">
        <QRCodeSVG value={link} size={132} level="M" />
      </div>
      {/* Bản lớn cho nút Tải QR — không hiện */}
      <div ref={canvasWrap} className="hidden" aria-hidden>
        <QRCodeCanvas value={link} size={640} level="M" marginSize={4} />
      </div>
      <div className="min-w-0 flex-1 space-y-3">
        <div className="flex items-center gap-2 rounded-md border border-rule bg-paperSoft p-1.5 pl-2.5">
          <span className="min-w-0 flex-1 truncate select-all font-mono text-xs text-ink" title={link}>
            {link}
          </span>
          <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={copy} aria-label="Sao chép link giới thiệu">
            {copied ? <Check className="text-ok" /> : <Copy />}
            <span className="max-sm:hidden">{copied ? 'Đã chép' : 'Sao chép'}</span>
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {canShare && (
            <Button type="button" size="sm" onClick={share}>
              <Share2 /> Chia sẻ
            </Button>
          )}
          <Button type="button" size="sm" variant="outline" onClick={downloadQr}>
            <Download /> Tải QR
          </Button>
        </div>
        <p className="text-xs text-muted">
          Mã giới thiệu: <span className="font-mono text-ink">{invite.code}</span>
        </p>
      </div>
    </div>
  )
}
